/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is an effect kind or a state code; it renders nothing */
// C1 Connectivity, the contract itself: pure, time explicit (ms), driven
// unchanged by connectivity_edges.json. src/lib/offline/connectivity.ts hosts
// it in the browser (navigator.onLine + online/offline events, a timer for
// the debounce and the probe schedule, apiFetch's reachability reports).
//
//  - The OS path is debounced (trailing, exactly 1,000 ms, both directions):
//    a raw value becomes effective only once nothing else arrived for 1 s.
//  - Every probe and API result carries the path epoch it STARTED under; a
//    result from another epoch is dropped, and a success never moves the
//    state to online while the raw or effective path is unsatisfied.
//  - online -> degraded after the 3rd API failure within 60 s; degraded and
//    offline -> online on the first success, emitting `recovered`.
//  - While degraded the probe runs at 5, 10, 20, 40 s, then every 60 s.
import { CONN, CONN_EVENT, type ConnectivityEvent, type ConnectivityState } from './offline.codes'

export const DEBOUNCE_MS = 1000
export const FAILURE_WINDOW_MS = 60_000
export const FAILURES_TO_DEGRADE = 3
export const DEGRADED_PROBE_SCHEDULE_MS = [5000, 10_000, 20_000, 40_000, 60_000] as const
export const PROBE_TIMEOUT_MS = 3000

export interface Transition {
  at: number
  to: ConnectivityState
  emits: ConnectivityEvent | null
}

export type Effect =
  | { kind: 'transition'; transition: Transition }
  /** Start the probe now; its result carries `epoch` back. */
  | { kind: 'start_probe'; epoch: number; at: number }

export class ConnectivityMachine {
  state: ConnectivityState
  /** The debounced path the transition table reads. */
  effectivePathSatisfied: boolean
  /** The latest raw OS value (effective only once stable for 1 s). */
  rawPathSatisfied: boolean
  /** Moves on every effective-path change. */
  pathEpoch = 0
  pendingRaw: { satisfied: boolean; deadline: number } | null = null
  probeInFlight: { epoch: number; startedAt: number } | null = null
  nextProbeAt: number | null = null
  private failureTimes: number[] = []
  private degradedProbeIndex = 0

  /** `state` at process start; the path is satisfied unless offline. */
  constructor(state: ConnectivityState) {
    this.state = state
    const satisfied = state !== CONN.offline
    this.effectivePathSatisfied = satisfied
    this.rawPathSatisfied = satisfied
  }

  /** The next time `advance` has something to do. */
  get nextDeadline(): number | null {
    const ds = [this.pendingRaw?.deadline, this.nextProbeAt].filter((d): d is number => typeof d === 'number')
    return ds.length ? Math.min(...ds) : null
  }

  /** Fire every deadline at or before `now`, oldest first (a debounce before a probe due at the same instant). */
  advance(now: number): Effect[] {
    const out: Effect[] = []
    for (;;) {
      const deadline = this.nextDeadline
      if (deadline === null || deadline > now) break
      if (this.pendingRaw && this.pendingRaw.deadline === deadline) {
        out.push(...this.settlePath(this.pendingRaw.satisfied, deadline))
      } else if (this.nextProbeAt === deadline) {
        this.nextProbeAt = null
        out.push(...this.startProbe(deadline))
      } else {
        break
      }
    }
    return out
  }

  /** A raw OS path value. */
  osPath(satisfied: boolean, now: number): Effect[] {
    const out = this.advance(now)
    this.rawPathSatisfied = satisfied
    this.pendingRaw = { satisfied, deadline: now + DEBOUNCE_MS }
    out.push(...this.advance(now))
    return out
  }

  /** An explicit probe start (start-up, a fixture). */
  probeNow(now: number): Effect[] {
    return [...this.advance(now), ...this.startProbe(now)]
  }

  /** The tab became visible: probe at once while not online and the OS reports a path. */
  foreground(now: number): Effect[] {
    const out = this.advance(now)
    if (this.state !== CONN.online && this.effectivePathSatisfied) out.push(...this.startProbe(now))
    return out
  }

  /** The probe answered. `epoch` is the path epoch it STARTED under. */
  probeResult(success: boolean, epoch: number, now: number): Effect[] {
    const out = this.advance(now)
    if (this.probeInFlight?.epoch === epoch) this.probeInFlight = null
    if (epoch !== this.pathEpoch || !this.rawPathSatisfied || !this.effectivePathSatisfied) return out
    if (success) {
      out.push(...this.reachedServer(now))
      return out
    }
    if (this.state === CONN.offline) {
      // The path is up, the API is not (a captive portal, DNS).
      this.state = CONN.degraded
      out.push({ kind: 'transition', transition: { at: now, to: CONN.degraded, emits: null } })
      this.degradedProbeIndex = 0
      this.scheduleDegradedProbe(now)
    } else if (this.state === CONN.degraded) {
      this.scheduleDegradedProbe(now)
    } else {
      // The start-up probe of an optimistic `online` failed.
      this.enterDegraded(now, out)
    }
    return out
  }

  /**
   * An API request answered (or did not). `success` is spec 0.6's C1 column:
   * an HTTP answer below 500 (a 401 included), not a 503. `epoch` is the path
   * epoch when the request STARTED.
   */
  apiResult(success: boolean, epoch: number, now: number): Effect[] {
    const out = this.advance(now)
    if (epoch !== this.pathEpoch) return out
    if (success) {
      if (!this.rawPathSatisfied || !this.effectivePathSatisfied) return out
      out.push(...this.reachedServer(now))
      return out
    }
    if (this.state !== CONN.online) return out
    this.failureTimes.push(now)
    this.failureTimes = this.failureTimes.filter(t => now - t <= FAILURE_WINDOW_MS)
    if (this.failureTimes.length >= FAILURES_TO_DEGRADE) this.enterDegraded(now, out)
    return out
  }

  private settlePath(satisfied: boolean, now: number): Effect[] {
    this.pendingRaw = null
    if (satisfied === this.effectivePathSatisfied) return []
    this.effectivePathSatisfied = satisfied
    this.pathEpoch += 1
    // A probe of the old path is abandoned; its late answer carries the old
    // epoch and is dropped.
    this.probeInFlight = null
    this.nextProbeAt = null
    this.failureTimes = []
    if (!satisfied) {
      if (this.state === CONN.offline) return []
      this.state = CONN.offline
      return [{ kind: 'transition', transition: { at: now, to: CONN.offline, emits: CONN_EVENT.wentOffline } }]
    }
    // Offline with a path again: stay offline and probe now.
    if (this.state !== CONN.offline) return []
    return this.startProbe(now)
  }

  private startProbe(now: number): Effect[] {
    if (this.probeInFlight !== null || !this.effectivePathSatisfied) return []
    this.probeInFlight = { epoch: this.pathEpoch, startedAt: now }
    this.nextProbeAt = null
    return [{ kind: 'start_probe', epoch: this.pathEpoch, at: now }]
  }

  private reachedServer(now: number): Effect[] {
    this.failureTimes = []
    if (this.state === CONN.online) return []
    this.state = CONN.online
    this.nextProbeAt = null
    this.degradedProbeIndex = 0
    return [{ kind: 'transition', transition: { at: now, to: CONN.online, emits: CONN_EVENT.recovered } }]
  }

  private enterDegraded(now: number, out: Effect[]): void {
    this.state = CONN.degraded
    this.failureTimes = []
    out.push({ kind: 'transition', transition: { at: now, to: CONN.degraded, emits: CONN_EVENT.degraded } })
    this.degradedProbeIndex = 0
    this.scheduleDegradedProbe(now)
  }

  private scheduleDegradedProbe(now: number): void {
    const s = DEGRADED_PROBE_SCHEDULE_MS
    const delay = s[Math.min(this.degradedProbeIndex, s.length - 1)]
    this.degradedProbeIndex += 1
    this.nextProbeAt = now + delay
  }
}
